import { useEffect, useState } from 'react';
import axios from 'axios';
import { ROLLOVER_HOUR } from '../functions/businessDay';

const clampPoints = (points: number) => {
    if (!points || points < 0) return 0;
    if (points > 5) return 5;
    return points;
};

/**
 * Looks up the active OtherFee tier for the current guest-points level.
 *
 * Time semantics: the OtherFee table can model after-midnight tiers as
 * `beforeHour > 24` (e.g. afterHour:22 / beforeHour:26 means "10 PM - 2 AM
 * next day"). The current wall-clock hour is rolled to 24+ when before
 * the 6 AM business-day rollover so the lookup matches Friday's late-
 * night tier instead of falling through to "before any tier" (which used
 * to return discount=0 incorrectly).
 */
const useDiscount = (action: string, guestPoints: number = 0) => {
    const [discount, setDiscount] = useState(0);
    const [loadingDiscount, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            if (action !== 'add') {
                return setLoading(false);
            }

            try {
                const points = clampPoints(guestPoints);

                const { data } = await axios.get(
                    `/api/fees?type=OtherFee&points=${points}`
                );

                if (!Array.isArray(data) || data.length === 0) {
                    return setDiscount(0);
                }

                const tiers = [...data].sort(
                    (a, b) => a.beforeHour - b.beforeHour
                );

                // Roll wall-clock hour to 24+ when before 6 AM rollover, so
                // Saturday 1 AM (= hour 25) hits Friday's late tier
                // (afterHour:22 / beforeHour:26) instead of returning 0.
                const raw = new Date().getHours();
                const hour = raw < ROLLOVER_HOUR ? raw + 24 : raw;

                const earliest = tiers[0];
                if (hour < earliest.afterHour) {
                    return setDiscount(0);
                }

                // No fallback to tiers[length-1] — tiers are time
                // windows. Outside any window, no rebate is active.
                // This matches the server-side fix in
                // PinkFeeService.resolveTimeDiscount.
                const activeTier = tiers.find(
                    (tier) => hour < tier.beforeHour
                );

                return setDiscount(activeTier ? activeTier.value : 0);
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        })();
    }, [action, guestPoints]);

    return {
        discount,
        setDiscount,
        loadingDiscount,
    };
};

export default useDiscount;
